import type { TextFieldProps } from '@mui/material'
import { TextField } from '@mui/material'
import type { ChangeEvent, FocusEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

type DecimalInputOwnProps = {
  /** The controlled numeric value. Use `null` for empty state. */
  value: number | null
  /** Callback fired when the value changes. Only called with valid complete numbers or null. */
  onValueChange: (value: number | null) => void
  /** Maximum number of decimal places allowed. Defaults to 2. */
  decimalPlaces?: number
  /** Minimum allowed value (inclusive). */
  min?: number
  /** Maximum allowed value (inclusive). */
  max?: number
  /** Whether to allow negative numbers. Defaults to false. */
  allowNegative?: boolean
  /** Whether to select all text when the input is focused. Defaults to false. */
  selectOnFocus?: boolean
}

export type DecimalInputProps = DecimalInputOwnProps &
  Omit<TextFieldProps, 'value' | 'onChange' | 'slotProps'> & {
    /** Props passed to the underlying HTML input element */
    slotProps?: Omit<TextFieldProps['slotProps'], 'htmlInput'> & {
      htmlInput?: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'inputMode'>
    }
  }

/**
 * A controlled decimal input that properly handles intermediate typing states.
 *
 * Unlike a standard number input, this component allows users to type decimal
 * points without the value being immediately parsed and losing the decimal.
 * For example, typing "1." will show "1." in the input rather than "1".
 *
 * The `onValueChange` callback is only called with valid complete numbers,
 * not intermediate states like "1." or "-".
 */
function DecimalInput({
  value,
  onValueChange,
  decimalPlaces = 2,
  min,
  max,
  allowNegative = false,
  selectOnFocus = false,
  onBlur,
  slotProps,
  ...textFieldProps
}: Readonly<DecimalInputProps>) {
  const inputRef = useRef<HTMLInputElement>(null)
  // Use local string state to allow intermediate values like "1." or "-"
  const [inputValue, setInputValue] = useState<string>(value?.toString() ?? '')

  // Sync local state when external value changes
  useEffect(() => {
    setInputValue(value?.toString() ?? '')
  }, [value])

  // Build validation regex based on props
  const buildValidationRegex = useCallback(() => {
    const negativePrefix = allowNegative ? '-?' : ''
    return new RegExp(`^${negativePrefix}\\d*\\.?\\d{0,${decimalPlaces}}$`)
  }, [allowNegative, decimalPlaces])

  const isWithinBounds = useCallback(
    (numValue: number): boolean => {
      if (min !== undefined && numValue < min) return false
      if (max !== undefined && numValue > max) return false
      return true
    },
    [min, max],
  )

  const clampValue = useCallback(
    (numValue: number): number => {
      let clamped = numValue
      if (min !== undefined && clamped < min) clamped = min
      if (max !== undefined && clamped > max) clamped = max
      return clamped
    },
    [min, max],
  )

  const isIntermediateState = useCallback(
    (val: string): boolean => {
      // Check for intermediate typing states that shouldn't trigger onValueChange
      if (val === '' || val === '.' || val === '-' || val === '-.') return true
      if (val.endsWith('.')) return true
      if (allowNegative && val === '-') return true
      return false
    },
    [allowNegative],
  )

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      const regex = buildValidationRegex()

      // Allow empty input
      if (newValue === '') {
        setInputValue('')
        onValueChange(null)
        return
      }

      // Only accept valid patterns
      if (regex.test(newValue)) {
        setInputValue(newValue)

        // Only update parent with valid complete numbers
        if (!isIntermediateState(newValue)) {
          const numericValue = Number.parseFloat(newValue)
          if (!Number.isNaN(numericValue) && isWithinBounds(numericValue)) {
            onValueChange(numericValue)
          }
        }
      }
    },
    [buildValidationRegex, isIntermediateState, isWithinBounds, onValueChange],
  )

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      // On blur, clean up intermediate states and clamp to bounds
      if (inputValue === '' || inputValue === '.' || inputValue === '-' || inputValue === '-.') {
        setInputValue('')
        onValueChange(null)
      } else {
        const numericValue = Number.parseFloat(inputValue)
        if (!Number.isNaN(numericValue)) {
          const clampedValue = clampValue(numericValue)
          setInputValue(clampedValue.toString())
          onValueChange(clampedValue)
        }
      }

      // Call the original onBlur if provided
      onBlur?.(event)
    },
    [clampValue, inputValue, onBlur, onValueChange],
  )

  const handleFocus = useCallback(() => {
    if (selectOnFocus) {
      // Defer select to ensure the input value is fully rendered
      requestAnimationFrame(() => {
        inputRef.current?.select()
      })
    }
  }, [selectOnFocus])

  return (
    <TextField
      {...textFieldProps}
      inputRef={inputRef}
      slotProps={{
        ...slotProps,
        htmlInput: {
          inputMode: 'decimal',
          ...slotProps?.htmlInput,
        },
      }}
      value={inputValue}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
    />
  )
}

export default DecimalInput
